// @flow
/* global SETTINGS: false */
import React from "react"
import R from "ramda"
import moment from "moment-timezone"

import type { Coupon } from "../../../flow/couponTypes"
import type { Course, CourseRun } from "../../../flow/programTypes"
import {
  COURSE_ACTION_REENROLL,
  STATUS_PAID_BUT_NOT_ENROLLED,
  STATUS_CAN_UPGRADE,
  DASHBOARD_FORMAT,
  COURSE_ACTION_ENROLL
} from "../../../constants"
import { S } from "../../../lib/sanctuary"
import {
  courseUpcomingOrCurrent,
  hasFailedCourseRun,
  futureEnrollableRun,
  isEnrollableRun,
  userIsEnrolled,
  isOfferedInUncertainFuture,
  notNilorEmpty,
  hasCanUpgradeCourseRun,
  hasMissedDeadlineCourseRun,
  hasCurrentlyEnrolledCourseRun
} from "./util"
import { hasPassedCourseRun } from "../../../lib/grades"
import { formatPrettyDateTimeAmPmTz, parseDateString } from "../../../util/date"

type Message = {
  message: string | React$Element<*>,
  action?: React$Element<*>
}

export const formatAction = (message: Message) => (
  <div className="second-col">{message.action}</div>
)

export const formatMessage = (message: Message, index?: number) => (
  <div className="status-message cols" key={index}>
    <div className="message first-col">{message.message}</div>
    {message.action ? formatAction(message) : null}
  </div>
)

export const formatDate = (date: ?string) =>
  moment(date).format(DASHBOARD_FORMAT)

type CalculateMessagesProps = {
  courseAction: (run: CourseRun, actionType: string) => React$Element<*>,
  firstRun: CourseRun,
  course: Course,
  expandedStatuses: Set<number>,
  setShowExpandedCourseStatus: (n: number) => void,
  setExamEnrollmentDialogVisibility: (b: boolean) => void,
  setSelectedExamCouponCourse: (n: number) => void,
  coupon?: Coupon
}

const courseStartMessage = (run: CourseRun) => {
  const startDate = notNilorEmpty(run.course_start_date)
    ? formatPrettyDateTimeAmPmTz(parseDateString(run.course_start_date))
    : run.fuzzy_start_date

  if (startDate) {
    if (moment(run.course_start_date).isBefore(moment(), "day")) {
      return `Course started on ${startDate}.`
    }
    return `Next course starts ${startDate}.`
  }
  return ""
}

const enrollmentDateMessage = (run: CourseRun) => {
  const enrollmentDate = notNilorEmpty(run.enrollment_start_date)
    ? formatDate(run.enrollment_start_date)
    : run.fuzzy_enrollment_start_date
  if (enrollmentDate) {
    const startText = isEnrollableRun(run) ? "started" : "starts"
    return ` Enrollment ${startText} ${enrollmentDate}.`
  }
  return ""
}

// this calculates any status messages we'll need to show the user
// we wrap the array of messages in a Maybe, so that we can indicate
// the case where there are no messages cleanly
//
// I'm so sorry for anyone who has to read / modify this
// sometimes there really isn't a better way :/
export const calculateMessages = (props: CalculateMessagesProps) => {
  const {
    courseAction,
    firstRun,
    course,
    expandedStatuses,
    setShowExpandedCourseStatus
  } = props

  const messages = []

  // If first run is paid but user never enrolled, most likely there was
  // problem enrolling, and first_unexpired_run is returned, so no need to check for past enrollment
  if (firstRun.status === STATUS_PAID_BUT_NOT_ENROLLED) {
    const contactHref = `mailto:${SETTINGS.support_email}`
    let date = ""
    if (isEnrollableRun(firstRun)) {
      date = `now`
    } else if (notNilorEmpty(firstRun.enrollment_start_date)) {
      date = formatDate(firstRun.enrollment_start_date)
    } else if (
      firstRun.fuzzy_start_date &&
      isOfferedInUncertainFuture(firstRun)
    ) {
      date = `in ${firstRun.fuzzy_start_date}`
    }
    return S.Just([
      {
        message: (
          <div>
            {`You're not enrolled in this course yet. You can enroll ${date}, or if you think there is a problem, `}
            <a href={contactHref}>contact us for help.</a>
          </div>
        ),
        action: courseAction(firstRun, COURSE_ACTION_ENROLL)
      }
    ])
  }

  // User never enrolled
  if (!R.any(userIsEnrolled, course.runs)) {
    if (isEnrollableRun(firstRun)) {
      messages.push({
        message: `${courseStartMessage(firstRun)}`,
        action:  courseAction(firstRun, COURSE_ACTION_ENROLL)
      })
    } else if (
      firstRun.fuzzy_start_date &&
      isOfferedInUncertainFuture(firstRun)
    ) {
      messages.push({
        message: `${courseStartMessage(firstRun)}`
      })
    } else {
      messages.push({
        message: "There are no future course runs scheduled."
      })
    }
  }

  if (course.certificate_url) {
    messages.push({
      message: (
        <div>
          {"You passed this course! "}
          <a
            href={course.certificate_url}
            target="_blank"
            rel="noopener noreferrer"
          >
            View Certificate
          </a>
          {S.maybe(
            null,
            () => [
              <span key="first">{" | "}</span>,
              <a
                key="second"
                onClick={() => setShowExpandedCourseStatus(course.id)}
              >
                Re-enroll
              </a>
            ],
            futureEnrollableRun(course)
          )}
        </div>
      )
    })
  }
  // course is passed and paid but no certificate yet
  if (!course.certificate_url && course.is_passed) {
    messages.push({
      message: "You passed this course."
    })
  }
  // handle other 'in-progress' cases
  if (
    firstRun.status === STATUS_CAN_UPGRADE &&
    courseUpcomingOrCurrent(firstRun)
  ) {
    const message = course.certificate_url
      ? "You are re-taking this course. Upgrades are no longer available."
      : "You are auditing. Upgrades are no longer available."
    messages.push({
      message
    })
    return S.Just(messages)
  }

  // all cases where courseRun is not currently in progress
  // passed means user also paid
  if (hasPassedCourseRun(course)) {
    // this is the expanded message, which we should show if the user
    // has clicked one of the 're-enroll' links
    if (expandedStatuses.has(course.id)) {
      messages.push(
        S.maybe(
          null,
          run => ({
            message: `Next course starts ${formatDate(run.course_start_date)}.`,
            action:  courseAction(run, COURSE_ACTION_REENROLL)
          }),
          futureEnrollableRun(course)
        )
      )
    }
    return S.Just(messages)
  } else if (hasCanUpgradeCourseRun(course)) {
    // the course finished but was audited
    messages.push({
      message:
        "The edX course is complete, but upgrades are no longer available."
    })
    return S.Just(messages)
  } else if (
    hasMissedDeadlineCourseRun(course) &&
    !hasCurrentlyEnrolledCourseRun(course)
  ) {
    // the course finished and the upgrade deadline passed
    const date = run => formatDate(run.course_start_date)
    const msg = run => {
      return `You missed the upgrade deadline, but you can re-enroll. Next course starts ${date(
        run
      )}.${enrollmentDateMessage(run)}`
    }
    messages.push(
      S.maybe(
        {
          message:
            "You missed the upgrade deadline and will not receive MicroMasters credit for this course. There are no future runs of this course scheduled at this time."
        },
        run => ({
          message: msg(run),
          action:  courseAction(run, COURSE_ACTION_REENROLL)
        }),
        futureEnrollableRun(course)
      )
    )
  }
  if (hasFailedCourseRun(course) && !hasPassedCourseRun(course)) {
    return S.Just(
      S.maybe(
        messages.concat({ message: "You did not pass the course." }),
        run =>
          messages.concat({
            message: `You did not pass the course, but you can re-enroll. ${courseStartMessage(
              run
            )}${enrollmentDateMessage(run)}`,
            action: courseAction(run, COURSE_ACTION_REENROLL)
          }),
        futureEnrollableRun(course)
      )
    )
  }

  return messages.length === 0 ? S.Nothing : S.Just(messages)
}

const formatMessages = R.addIndex(R.map)(formatMessage)

const wrapMessages = messages => (
  <div className="course-status-messages">{messages}</div>
)

const StatusMessages = R.compose(
  S.maybe(null, wrapMessages),
  S.map(formatMessages),
  calculateMessages
)

export default StatusMessages
